import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TextField,
  Typography
} from '@mui/material';

const initials = (name) => String(name || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join('')
  .toUpperCase();

function TeamGroupDialog({ open, employees, group, saving, error, onClose, onSave }) {
  const initialIds = useMemo(() => (group?.participants || [])
    .filter((participant) => participant.type === 'employee')
    .map((participant) => String(participant.employeeId)), [group]);
  const [name, setName] = useState('');
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!open) return;
    setName(group?.name || '');
    setSelected(initialIds);
  }, [group, initialIds, open]);

  const selectableEmployees = employees.filter((employee) => employee.id !== 'tower' && !employee.isSelf);
  const toggle = (employeeId) => setSelected((current) => current.includes(employeeId)
    ? current.filter((id) => id !== employeeId)
    : [...current, employeeId]);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{group ? 'Manage group' : 'New team group'}</DialogTitle>
      <DialogContent dividers sx={{ px: 2 }}>
        <TextField
          autoFocus
          fullWidth
          label="Group name"
          size="small"
          value={name}
          onChange={(event) => setName(event.target.value)}
          inputProps={{ maxLength: 120 }}
        />
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Members
        </Typography>
        <List disablePadding sx={{ maxHeight: 330, overflowY: 'auto' }}>
          {selectableEmployees.map((employee) => (
            <ListItemButton
              key={employee.id}
              dense
              onClick={() => toggle(String(employee.id))}
              sx={{ borderRadius: 1 }}
            >
              <Checkbox
                edge="start"
                checked={selected.includes(String(employee.id))}
                tabIndex={-1}
                disableRipple
              />
              <ListItemAvatar sx={{ minWidth: 42 }}>
                <Avatar sx={{ width: 30, height: 30, fontSize: 11 }}>{initials(employee.name)}</Avatar>
              </ListItemAvatar>
              <ListItemText primary={employee.name} secondary={employee.role} />
            </ListItemButton>
          ))}
        </List>
        {error ? <Box mt={1}><Typography variant="caption" color="error">{error}</Typography></Box> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          disabled={saving || name.trim().length < 2 || selected.length === 0}
          onClick={() => onSave({ name: name.trim(), employeeIds: selected })}
        >
          {saving ? 'Saving...' : group ? 'Save group' : 'Create group'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TeamGroupDialog;
